import clsx from 'clsx'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { Button, Grid, Icon, NetworkLogo } from 'gu-ui-kit'
import { IS_APP_PRODUCT_ADSIMPLE } from 'utils/constants/product'
import Nav from 'components/Nav'
import Footer from 'components/Footer'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import { APP_PRODUCT } from 'constants/app-config'
import defaultNavigationConfig from 'constants/navigation'
import { useDispatch, useSelector } from 'react-redux'
import DrawerFooter from 'components/Drawer/subcomponents/DrawerFooter'
import BalanceWidget from 'components/Drawer/subcomponents/BalanceWidget'
import { Link } from 'react-router-dom'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { ROUTES } from 'constants/routes'
import { SET_DRAWER_STATE } from 'modules/App/types'
import { selectAppUser, selectAppDrawerState } from 'modules/App/store/selectors'
import findMatchedRoutes, { getLastMatchedRoute } from 'utils/findMatchedRoutes'
import styles from 'components/Drawer/drawer.module.scss'
// const scrollUpThreashold = 0

const Drawer = ({ children, className }) => {
  const breakpoints = useBreakpoint()
  const user = useSelector(selectAppUser) ?? {}
  const drawerState = useSelector(selectAppDrawerState)
  const pathGen = useGeneratePathI18n()
  const asideRef = useRef(null)
  const collapse = useMemo(() => drawerState.collapsed, [drawerState.collapsed])
  const dispatch = useDispatch()

  const matchedRoutes = findMatchedRoutes()

  const closeSidebar = useCallback(() => {
    dispatch({ type: SET_DRAWER_STATE, payload: { opened: false } })
  }, [])

  useEffect(() => {
    document.querySelector('body').classList.toggle('body_overflow_hidden', drawerState.opened)
    document.querySelector('body').classList.toggle('detached_drawer_showen', drawerState.opened)
  }, [drawerState])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!drawerState.opened) return

      if (asideRef.current) {
        const rect = asideRef.current.getBoundingClientRect()
        const { clientX, clientY } = e

        if (
          clientX < rect.left ||
          clientX > rect.right ||
          clientY < rect.top ||
          clientY > rect.bottom
        ) {
          closeSidebar()
        }
      }
    }

    const handleKeyDown = (e) => {
      if (!drawerState.opened) return
      if (e.key === 'Escape') {
        closeSidebar()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [!breakpoints.xl, closeSidebar, drawerState.opened])

  return (
    <>
      <aside
        className={clsx(
          styles['drawer'],
          {
            [styles['drawer_opened']]: drawerState.opened,
            [styles['drawer_collapsed']]: collapse && breakpoints.xl,
            [styles['drawer_detached']]: !breakpoints.xl,
          },
          className,
        )}
      >
        <div
          className={clsx(styles['drawer-inner'], 'layout-tile')}
          ref={asideRef}
        >
          <Grid
            direction={'column'}
            style={{ flexGrow: 1 }}
            className={styles['drawer-inner__grid']}
          >
            {breakpoints.xl && (
              <Grid
                direction={'row'}
                wrap={false}
                justifyContent={'space-between'}
                alignItems={'center'}
                className={clsx('margin-bottom-24', styles['drawer-inner__grid_head'])}
              >
                <Link to={pathGen(ROUTES.SERVICES)}>
                  <NetworkLogo
                    name={APP_PRODUCT}
                    color={IS_APP_PRODUCT_ADSIMPLE ? 'Interface-GU-Primary' : 'Interface-Eleven'}
                    type={collapse ? 'unlinked' : 'component'}
                    width={collapse ? 36 : 106}
                    height={collapse ? 36 : 36}
                  />
                </Link>

                <Button
                  className={clsx(styles['drawer-toggler'], {
                    [styles['drawer-toggler_collapsed']]: collapse,
                  })}
                  variant={'ghost'}
                  color={'Interface-Eighth'}
                  noLabel
                  size={'s'}
                  onClick={() =>
                    dispatch({ type: SET_DRAWER_STATE, payload: { collapsed: !collapse } })
                  }
                  startIcon={<Icon name={collapse ? 'arrow-next' : 'arrow-previous'} />}
                />
              </Grid>
            )}

            {breakpoints.xl && (
              <Grid
                fluid
                alignContent={'center'}
                direction={'row'}
                justifyContent={'center'}
              >
                <BalanceWidget
                  className={clsx('margin-bottom-24', styles['drawer-inner__grid_balance'])}
                  collapse={collapse && breakpoints.xl}
                />
              </Grid>
            )}

            <Nav
              className={styles['drawer-inner__grid_top']}
              items={defaultNavigationConfig({ user })}
              collapse={collapse && breakpoints.xl}
            />
            <Grid>
              <DrawerFooter collapse={collapse && breakpoints.xl} />
            </Grid>
          </Grid>
        </div>
      </aside>
      <main
        className={clsx(styles['content'], {
          [styles['content_wide']]: collapse,
        })}
      >
        <div
          className={clsx({
            ['layout-tile']: !getLastMatchedRoute(matchedRoutes)?.restyled,
          })}
        >
          {children}
        </div>
        <Footer />
      </main>
    </>
  )
}

Drawer.propTypes = {
  collapse: PropTypes.bool,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.element),
    PropTypes.element,
    PropTypes.oneOf([null]),
  ]),
  className: PropTypes.string,
}

export default Drawer
